import React, { useState } from "react";

import TextField from "../../../../components/input/textfield/TextField";
import TextArea from "../../../../components/input/textarea/TextArea";
import StatusAlert from "../../../../components/alerts/StatusAlert";

import { FormSection } from "../../../../components/layout/forms/FormSection";

import "./CreatorCreateGroupForm.css";

import { generateGroupCode } from "../../../../utils/helpers/generator/generateGroupCode";

import { useCreatorGroups } from "../hooks/useCreatorGroups";

const createInitialFormData = () => ({
  groupName: "",
  groupCode: generateGroupCode(6).toUpperCase(),
  groupPolicy: "",
});

const CreatorCreateGroupForm = ({ username, onClose, onSuccess }) => {
  const { loading, error, createCreatorGroup } = useCreatorGroups(username);

  const [formData, setFormData] = useState(createInitialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const saving = loading || submitting;

  const groupName = formData.groupName.trim();
  const groupCode = formData.groupCode.trim();
  const groupPolicy = formData.groupPolicy.trim();

  const isSubmitDisabled = saving || !groupName || !groupCode || !groupPolicy;

  const statusAlert = formError
    ? {
        type: "error",
        message: formError,
      }
    : error;

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));

    if (formError) {
      setFormError("");
    }
  };

  const resetForm = () => {
    setFormData(createInitialFormData());
    setFormError("");
  };

  const handleClose = () => {
    if (saving) {
      return;
    }

    resetForm();
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (saving) {
      return;
    }

    if (!username) {
      setFormError("Creator information is missing.");

      return;
    }

    if (!groupName) {
      setFormError("Group name is required.");

      return;
    }

    if (!groupCode) {
      setFormError("Group code is required.");

      return;
    }

    if (!groupPolicy) {
      setFormError("Group privacy and rules are required.");

      return;
    }

    try {
      setSubmitting(true);
      setFormError("");

      const result = await createCreatorGroup({
        groupName,
        groupCode,
        groupPolicy,
      });

      if (result === false || result?.success === false) {
        setFormError(result?.message || "Failed to create group.");

        return;
      }

      resetForm();

      await onSuccess?.(result);

      onClose?.();
    } catch (submitError) {
      setFormError(
        submitError?.response?.data?.message ||
          submitError?.message ||
          "Failed to create group.",
      );
    } finally {
      setSubmitting(false);
      onSuccess();
    }
  };

  return (
    <form className="creator-create-group-form" onSubmit={handleSubmit}>
      {statusAlert && (
        <div className="creator-create-group-form__alert">
          <StatusAlert status={statusAlert} />
        </div>
      )}

      <FormSection className="creator-create-group-form__fields">
        <TextField
          label="Group Name"
          name="groupName"
          value={formData.groupName}
          onChange={handleChange}
          placeholder="e.g. European Tour"
          maxLength={80}
          required
          disabled={saving}
        />

        <TextField
          label="Group Code"
          name="groupCode"
          value={formData.groupCode}
          placeholder="Group access code"
          required
          disabled
        />

        <TextArea
          label="Group Privacy & Rules"
          name="groupPolicy"
          value={formData.groupPolicy}
          onChange={handleChange}
          placeholder="Explain the group rules and how members should use its content."
          maxLength={1000}
          maxHeight={140}
          required
          disabled={saving}
        />
      </FormSection>

      <div className="creator-create-group-form__actions">
        <button
          type="button"
          className="creator-create-group-form__cancel"
          onClick={handleClose}
          disabled={saving}
        >
          Cancel
        </button>

        <button
          type="submit"
          className="creator-create-group-form__submit"
          disabled={isSubmitDisabled}
        >
          {saving ? "Creating..." : "Create Group"}
        </button>
      </div>
    </form>
  );
};

export default CreatorCreateGroupForm;
